<script lang="ts" setup>
import {
  AisInstantSearchSsr,
  AisAutocomplete,
  AisHits,
  AisConfigure,
  createServerRootMixin,
} from 'vue-instantsearch/vue3/es'
import { liteClient as algoliaSearch } from 'algoliasearch/lite'
import { IconObjectMagnifyingGlass } from '@cypress-design/vue-icon'
import { provide, ref, watch } from 'vue'

const searchClient = algoliaSearch(
  import.meta.env.PUBLIC_ALGOLIA_APP_ID.trim(),
  import.meta.env.PUBLIC_ALGOLIA_SEARCH_ONLY_API_KEY.trim(),
)

const props = withDefaults(
  defineProps<{
    indexName: string
    placeholder?: string
    hitsPerPage?: number
  }>(),
  {
    placeholder: undefined,
    hitsPerPage: 5,
  },
)

// trick to use the mixin in a setup() function if we know what it does
const { provide: mixinProvide, data: mixinData } = createServerRootMixin({
  searchClient,
  indexName: props.indexName,
})

const mixinProvidedElements = mixinProvide.apply(mixinData())
for (const key in mixinProvidedElements) {
  provide(key, mixinProvidedElements[key])
}

const active = ref(false)

function blur() {
  setTimeout(() => {
    active.value = false
  }, 200)
}

const currentRefinement = ref('')

watch([active, currentRefinement], ([value]) => {
  if (value) {
    keyboardHighlighted.value = -1
  }
})

const keyboardHighlighted = ref(-1)

const emit = defineEmits<{
  (event: 'prevItem', keyboardHighlighted: number): boolean
  (event: 'nextItem', keyboardHighlighted: number): boolean
}>()

function keyUpArrow() {
  const emitResult = emit('prevItem', keyboardHighlighted.value)

  if (emitResult === false) return
  if (!emitResult && keyboardHighlighted.value < 0) return
  keyboardHighlighted.value--
}

function keyDownArrow(numberOfItems: number) {
  const emitResult = emit('nextItem', keyboardHighlighted.value)

  if (emitResult === false) return
  if (!emitResult && keyboardHighlighted.value > numberOfItems) return
  keyboardHighlighted.value++
}

function goToLink(url: string) {
  if (!url?.length) return
  if (url.startsWith('http')) {
    window.open(url, '_blank')
    return
  }
  window.location.href = url
}
</script>

<template>
  <div class="relative z-20 mx-auto h-[52px] max-w-[592px] rounded-3xl border border-jade-100 bg-white">
    <!-- avoids ssr blink -->
    <div class="absolute flex h-full items-center text-gray-400" aria-hidden="true">
      <IconObjectMagnifyingGlass fill-color="jade-200" stroke-color="teal-500" class="ml-[20px] mr-[12px]" />
      <span>{{ placeholder }}</span>
    </div>
    <AisInstantSearchSsr :search-client="searchClient" :index-name="indexName">
      <AisConfigure :hits-per-page.camel="hitsPerPage">
        <AisAutocomplete
          :class-names="{
            'ais-SearchBox-form': 'flex justify-between rounded-full',
          }"
          :show-reset="false"
          class="relative z-50"
        >
          <template #default="{ refine, indices }">
            <IconObjectMagnifyingGlass
              fill-color="jade-200"
              stroke-color="teal-500"
              class="absolute left-[20px] top-[16px]"
            />
            <input
              v-model="currentRefinement"
              type="search"
              class="mr-[8px] h-[48px] w-full flex-1 rounded-full bg-white py-[8px] pl-[48px] pr-[16px] focus:outline-none"
              :placeholder="placeholder"
              @keydown.arrow-up.prevent="keyUpArrow"
              @keydown.arrow-down.prevent="keyDownArrow(indices[0].hits.length - 1)"
              @keydown.enter.prevent="goToLink(indices[0].hits[keyboardHighlighted].url)"
              @focus="active = true"
              @blur="blur"
              @input="refine(currentRefinement)"
            />
          </template>
        </AisAutocomplete>

        <slot
          v-if="active && currentRefinement.length > 0"
          name="allHits"
          v-bind="{ keyboardHighlighted, currentRefinement }"
        >
          <AisHits>
            <template #item="{ item: hit, index }">
              <slot v-bind="{ ...hit, index, keyboardHighlighted }" name="hit">
                <a :href="hit.url">
                  {{ hit.post_title }}
                </a>
              </slot>
            </template>
          </AisHits>
        </slot>
      </AisConfigure>
    </AisInstantSearchSsr>
  </div>
</template>

<style scoped>
::v-deep(.ais-Hits) {
  @apply my-4 rounded-3xl bg-white p-4;
}
</style>
